import { useCallback, useEffect, useMemo } from 'react'
import { FormFieldType } from '@open-tender/types'
import { useLoginForm } from '@open-tender/utils'
import {
  closeModal,
  loginCustomer,
  loginCustomerThanx,
  resetLoginError,
  resetOneTimePasscode,
  selectBrand,
  selectCustomer,
  selectOneTimePasscode,
  selectRecaptcha,
  signInCustomer,
  useAppDispatch,
  useAppSelector,
} from '@open-tender/cloud'
import { ButtonSubmit, FormError, FormInputs, FormSubmit } from 'components'
import { ThirdPartyLoyaltySignUpMessage } from 'components/ThirdPartyLoyalty'
import { sendLogin } from 'app/analytics'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'

const LoginForm = ({
  callback,
  requiredEmail,
}: {
  callback?: () => void
  requiredEmail?: string
}) => {
  const dispatch = useAppDispatch()
  const { has_thanx, tpls } = useAppSelector(selectBrand) || {}
  const { executeRecaptcha } = useGoogleReCaptcha()
  const hasPunchh = tpls === 'PUNCHH'
  const customer = useAppSelector(selectCustomer)
  const signIn = useAppSelector(selectOneTimePasscode)
  const { login: includeRecaptcha } = useAppSelector(selectRecaptcha) || {}

  const loading = useMemo(
    () => (hasPunchh ? signIn.loading : customer.loading),
    [hasPunchh, customer.loading, signIn.loading]
  )
  const error = useMemo(
    () => (hasPunchh ? signIn.error : customer.error),
    [hasPunchh, customer.error, signIn.error]
  )
  const showSignUpMessage =
    hasPunchh && error?.code === 'MEMBER_SIGNUP_REQUIRED'

  const loginDefault = useCallback(
    (email: string, password?: string, recaptcha_token?: string) =>
      dispatch(
        loginCustomer({ email, password: password as string, recaptcha_token })
      ),
    [dispatch]
  )
  const loginThanx = useCallback(
    (email: string, _?: string, recaptcha_token?: string) =>
      dispatch(loginCustomerThanx({ email, recaptcha_token })),
    [dispatch]
  )
  const loginTpls = useCallback(
    (email: string, password?: string, recaptcha_token?: string) =>
      dispatch(signInCustomer({ data: { email, password, recaptcha_token } })),
    [dispatch]
  )
  const login = has_thanx ? loginThanx : hasPunchh ? loginTpls : loginDefault

  const loginCallback = () => {
    sendLogin(data.email)
    callback?.() || dispatch(closeModal())
  }
  const getRecaptchaToken = async () =>
    (await executeRecaptcha?.('login')) || null

  const {
    submitRef,
    fields,
    data,
    errors,
    submitting,
    handleChange,
    handleSubmit,
  } = useLoginForm(
    loading,
    error,
    login,
    includeRecaptcha,
    loginCallback,
    !has_thanx,
    requiredEmail,
    getRecaptchaToken
  )
  const errMsg = error?.message || errors.form || ''

  const onChange = (field: FormFieldType, value: string | number | boolean) => {
    handleChange(field.name, value)
  }

  useEffect(() => {
    dispatch(resetLoginError())
    dispatch(resetOneTimePasscode())
    return () => {
      dispatch(resetLoginError())
      dispatch(resetOneTimePasscode())
    }
  }, [dispatch])

  return (
    <form id="login-form" onSubmit={handleSubmit} noValidate>
      {showSignUpMessage ? (
        <ThirdPartyLoyaltySignUpMessage />
      ) : (
        <FormError errMsg={errMsg} style={{ margin: '0 0 2rem' }} />
      )}
      <FormInputs
        fields={fields}
        data={data}
        onChange={onChange}
        errors={errors}
      />
      <FormSubmit>
        <ButtonSubmit submitRef={submitRef} submitting={submitting}>
          {submitting ? 'Submitting...' : 'Submit'}
        </ButtonSubmit>
      </FormSubmit>
    </form>
  )
}

export default LoginForm
